import React, { forwardRef } from "react"
// eslint-disable-next-line no-restricted-imports
import { Text, type TextProps } from "react-native"
import { cn } from "@/components/util/cn"
import { TextClassContext } from "@/components/reusables/ui/text"

interface BFTextProps extends TextProps {
  fsClass?: string
  variant?: BFTextVariantType
  color?: BFTextColor
  className?: string
  ref?: React.ForwardedRef<typeof BFText>
}

export const BFTextVariants = {
  standard: "color-darkest text-[16px]", // This is the old BFText default

  // The following styles are mastered in Figma. If Figma does update, so
  // should these. All text styles used in Figma should match these.

  // Content - Space Grotesque
  // https://www.figma.com/design/YpuCSsxiRhyW0mSijUmd2u/Product-Design?node-id=1375-17624&t=kKZbifQwARDHUDBZ-0

  Display: "font-spacegrotesk-bold text-[60px] leading-[50px] tracking-[0.6px]",
  HeroTitle: "font-figtree-extrabold text-[45px] leading-[45px] text-center",
  Title: "font-spacegrotesk-bold text-[20px] leading-[22px] tracking-[0.2px]",
  Subtitle: "font-spacegrotesk text-[16px] leading-[20px] tracking-[0.16px]",
  Body: "font-spacegrotesk text-[14px] leading-[18px] tracking-[0.14px]",
  BodyBold:
    "font-spacegrotesk-bold text-[14px] leading-[18px] tracking-[0.14px]",
  Small: "font-spacegrotesk text-[12px] leading-[16px] tracking-[0.12px]",
  SmallBold:
    "font-spacegrotesk-bold text-[12px] leading-[16px] tracking-[0.12px]",
  Tiny: "font-spacegrotesk text-[10px] leading-[12px] tracking-[0.2px]",

  // Headers - Figtree
  // https://www.figma.com/design/YpuCSsxiRhyW0mSijUmd2u/Product-Design?node-id=1375-17674&t=kKZbifQwARDHUDBZ-0

  H_Display:
    "font-figtree-extrabold text-[60px] leading-[60px] tracking-[-0.112px]",
  H1: "font-figtree-extrabold text-[28px] leading-[30px] tracking-[-0.112px]",
  H2: "font-figtree-extrabold text-[24px] leading-[28px] tracking-[-0.096px]",
  H3: "font-figtree-extrabold text-[18px] leading-[20px] tracking-[-0.072px]",
  H4: "font-figtree-extrabold text-[16px] leading-[20px] tracking-[0.16px]",
  H5: "font-figtree-extrabold text-[14px] leading-[18px] tracking-[0.14px]",
  H6: "font-figtree-extrabold text-[12px] leading-[16px] tracking-[0.12px]",
  H7: "font-figtree-extrabold text-[10px] leading-[12px] tracking-[0.01px]",
}

export type BFTextVariantType = keyof typeof BFTextVariants

const BFTextColors = {
  default: "text-midnight-blue",
  primary: "text-midnight-blue",
  primaryLight: "text-calm-blue",
  teal: "text-teal",
  white: "text-off-white",

  error: "text-red-orange",

  alternative: "text-off-white",
  hint: "text-warm-grey",
  disabled: "text-warm-grey",
}

type BFTextColor = keyof typeof BFTextColors

const BFText = React.forwardRef<Text, BFTextProps>(
  (
    {
      variant,
      children,
      color,
      allowFontScaling = false,
      fsClass = "fs-unmask",
      className,
      ...props
    },
    ref
  ) => {
    const textClass = React.useContext(TextClassContext)
    return (
      <Text
        fsClass={fsClass}
        allowFontScaling={allowFontScaling}
        className={cn(
          variant && BFTextVariants[variant],
          color && BFTextColors[color],
          textClass,
          className
        )}
        ref={ref}
        {...props}
      >
        {children}
      </Text>
    )
  }
)

export default BFText

type NoVariantTextProps = Omit<BFTextProps, "variant">
type NoColorTextProps = Omit<BFTextProps, "color">

export const Display = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="Display" {...props} ref={ref} />
})

export const Title = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="Title" {...props} ref={ref} />
})

export const Subtitle = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="Subtitle" {...props} ref={ref} />
})

export const Body = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="Body" {...props} ref={ref} />
})

export const BodyBold = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="BodyBold" {...props} ref={ref} />
})

export const Small = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="Small" {...props} ref={ref} />
})

export const SmallBold = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="SmallBold" {...props} ref={ref} />
})

export const Tiny = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="Tiny" {...props} ref={ref} />
})

export const HDisplay = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="H_Display" {...props} ref={ref} />
})

export const H1 = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="H1" {...props} ref={ref} />
})

export const H2 = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="H2" {...props} ref={ref} />
})

export const H3 = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="H3" {...props} ref={ref} />
})

export const H4 = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="H4" {...props} ref={ref} />
})

export const H5 = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="H5" {...props} ref={ref} />
})

export const H6 = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="H6" {...props} ref={ref} />
})

export const H7 = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="H7" {...props} ref={ref} />
})

export const HeroTitle = forwardRef<Text, NoVariantTextProps>((props, ref) => {
  return <BFText variant="HeroTitle" {...props} ref={ref} />
})

export const TealText = forwardRef<Text, NoColorTextProps>((props, ref) => {
  return <BFText color={"teal"} {...props} ref={ref} />
})
